import React, { useMemo } from 'react'
import {
  ActionButtons,
  Badge,
  DataTable,
  type DataTableColumn,
} from '@enamelai/design-system'
import { Icon as DeleteIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as ShieldIcon } from '@enamelai/design-system/icons/Shield'
import { Icon as ShieldOutlineIcon } from '@enamelai/design-system/icons/Shield_outline'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import {
  getInsurancePlanStatusColor,
  useInsuranceBadgeMode,
} from '@components/pages/Insurance/insuranceBadge'
import { InsurancePlan as InsurancePlanType } from '@dataTypes/insurance.types'

interface InsurancePlanProps {
  insurancePlans: InsurancePlanType[]
  selectedIndex: number
  onSelectInsurancePlan: (index: number) => void
  handleSetPrimaryInsurancePlanClick: (insurance_plan_id: string) => void
  handleDeleteInsurancePlanClick: (insurance_plan_id: string) => void
  outlined?: boolean
}

type InsurancePlanRow = { id: string }

type InsurancePlanEntry = {
  index: number
  plan: InsurancePlanType & { subscriber_dob?: string }
}

const getPlanRowId = (plan: InsurancePlanType, index: number): string =>
  plan.insurance_plan_id ?? `insurance-plan-${index}`

const InsurancePlan: React.FC<InsurancePlanProps> = ({
  insurancePlans,
  selectedIndex,
  onSelectInsurancePlan,
  handleSetPrimaryInsurancePlanClick,
  handleDeleteInsurancePlanClick,
  outlined = false,
}) => {
  const badgeMode = useInsuranceBadgeMode()

  const { rows, entriesById } = useMemo(() => {
    const entries = new Map<string, InsurancePlanEntry>()
    const planRows: InsurancePlanRow[] = insurancePlans.map((plan, index) => {
      const id = getPlanRowId(plan, index)
      entries.set(id, { index, plan })
      return { id }
    })
    return { rows: planRows, entriesById: entries }
  }, [insurancePlans])

  const selectedRowIds = useMemo(() => {
    const selectedPlan = insurancePlans[selectedIndex]
    return selectedPlan ? [getPlanRowId(selectedPlan, selectedIndex)] : []
  }, [insurancePlans, selectedIndex])

  const columns = useMemo<DataTableColumn<InsurancePlanRow>[]>(() => {
    const planOf = (id: string): InsurancePlanEntry['plan'] | undefined =>
      entriesById.get(id)?.plan
    return [
      {
        id: 'number',
        label: '#',
        width: 48,
        renderCell: ({ id }) => (
          <span data-insurance-plan-id={id}>{planOf(id)?.number}</span>
        ),
      },
      {
        id: 'carrier_name',
        label: 'Carrier',
        width: 220,
        renderCell: ({ id }) => {
          const plan = planOf(id)
          if (!plan) return null
          return (
            <span className="insurance-plan-carrier">
              {plan.carrier_name}
              {plan.is_primary && plan.carrier_name !== '-' && (
                <Badge label="Primary" color="teal" mode={badgeMode} />
              )}
            </span>
          )
        },
      },
      {
        id: 'plan_name',
        label: 'Plan',
        renderCell: ({ id }) => planOf(id)?.plan_name,
      },
      {
        id: 'plan_type',
        label: 'Plan type',
        renderCell: ({ id }) => planOf(id)?.plan_type || '-',
      },
      {
        id: 'subscriber_fullname',
        label: 'Subscriber',
        renderCell: ({ id }) => planOf(id)?.subscriber_fullname,
      },
      {
        id: 'subscriber_member_id',
        label: 'Subscriber ID',
        renderCell: ({ id }) => planOf(id)?.subscriber_member_id,
      },
      {
        id: 'subscriber_dob',
        label: 'Subscriber DOB',
        renderCell: ({ id }) => planOf(id)?.subscriber_dob ?? '-',
      },
      {
        id: 'status',
        label: 'Status',
        renderCell: ({ id }) => {
          const status = planOf(id)?.status
          if (!status || status === '-') return '-'
          return (
            <Badge
              label={status}
              color={getInsurancePlanStatusColor(status)}
              mode={badgeMode}
            />
          )
        },
      },
    ]
  }, [badgeMode, entriesById])

  const handleTableClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement
    if (target.closest('button, a, input, textarea, select, [role="menu"]')) {
      return
    }
    const planId = target
      .closest('tr.ds-data-table__row')
      ?.querySelector<HTMLElement>('[data-insurance-plan-id]')
      ?.getAttribute('data-insurance-plan-id')
    const entry = planId ? entriesById.get(planId) : undefined
    if (entry) onSelectInsurancePlan(entry.index)
  }

  return (
    <div className="section-insurance-plan">
      {insurancePlans.length === 0 ? (
        <InsuranceEmptyCard
          iconClass="mgc mgc_shield_fill"
          emptyTitle="No insurance plans found"
          description="You don't have any insurance plans on record yet."
          outlined={outlined}
        />
      ) : (
        <>
          <p className="section-title">Insurance plan</p>
          <div
            className="insurance-table insurance-table--selectable"
            onClick={handleTableClick}
          >
            <DataTable<InsurancePlanRow>
              columns={columns}
              rows={rows}
              selectedRowIds={selectedRowIds}
              actionsLabel="Actions"
              renderActions={({ id }) => {
                const plan = entriesById.get(id)?.plan
                if (!plan || plan.status === '-') return null
                const planId = plan.insurance_plan_id ?? id
                return (
                  <ActionButtons
                    size="sm"
                    actions={[
                      {
                        id: 'set-primary',
                        label: plan.is_primary
                          ? 'Primary plan'
                          : 'Set this plan as primary',
                        icon: plan.is_primary ? (
                          <ShieldIcon
                            className="ds-action-button__svg"
                            aria-hidden="true"
                          />
                        ) : (
                          <ShieldOutlineIcon
                            className="ds-action-button__svg"
                            aria-hidden="true"
                          />
                        ),
                        tone: 'primary',
                        variant: plan.is_primary ? 'solid' : 'outline',
                        size: 'sm',
                        onClick: () =>
                          handleSetPrimaryInsurancePlanClick(planId),
                      },
                      {
                        id: 'delete',
                        label: 'Delete this plan',
                        icon: (
                          <DeleteIcon
                            className="ds-action-button__svg"
                            aria-hidden="true"
                          />
                        ),
                        tone: 'danger',
                        variant: 'outline',
                        size: 'sm',
                        onClick: () => handleDeleteInsurancePlanClick(planId),
                      },
                    ]}
                  />
                )
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default InsurancePlan
